import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCleanupOnUnload } from "../hooks/useCleanupOnUnload";

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token"), [searchParams]);
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { resetPassword } = useAuth();

  // Clear localStorage when tab is closed
  useCleanupOnUnload();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!token) {
      setError("Invalid or missing reset token.");
      return;
    }

    setLoading(true);
    const result = await resetPassword(token, newPassword);

    if (result.success) {
      setSuccess("Password reset successful. Please log in again.");
      setNewPassword("");
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  return (
    <div className="h-screen max-h-screen overflow-hidden overflow-x-hidden flex items-center justify-center p-4 sm:p-5 bg-gradient-to-br from-indigo-600 to-purple-600">
      <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-10 w-full max-w-md shadow-[0_10px_40px_rgba(0,0,0,0.1)]">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1 sm:mb-2 text-center">
          Reset Password
        </h1>
        <p className="text-sm sm:text-base text-gray-500 mb-6 sm:mb-8 text-center">
          Enter a new password (min 8 characters)
        </p>

        {error && (
          <div className="mb-4 sm:mb-5 rounded-lg sm:rounded-lg border border-red-200 bg-red-50 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 sm:mb-5 rounded-lg sm:rounded-lg border border-green-200 bg-green-50 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-green-700">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:gap-5">
          <div className="flex flex-col">
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="rounded-lg sm:rounded-xl border-2 border-gray-200 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/10"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="rounded-lg sm:rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold px-4 py-2 sm:py-3 text-sm sm:text-base disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-lg transition"
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>

        <p className="text-center mt-4 sm:mt-5 text-xs sm:text-sm text-gray-500">
          <Link
            to="/login"
            className="text-indigo-600 font-semibold hover:underline"
          >
            Back to Login
          </Link>
        </p>
      </div>
    </div>
  );
}

export default ResetPassword;
